import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Globe, Search, Users, FileText, Phone, ArrowLeft, ExternalLink, RefreshCw, Lock, Gift, ShoppingBag, Palette } from "lucide-react";
import { Button } from "../ui/button";

export const NAV = [
  { id: "overview", label: "Übersicht", icon: LayoutDashboard },
  { id: "website", label: "Webseite", icon: Globe },
  { id: "seo", label: "SEO & Google", icon: Search },
  { id: "leads", label: "Kunden", icon: Users },
  { id: "shop", label: "Shop & Zahlungen", icon: ShoppingBag },
  { id: "branding", label: "Logo & Visitenkarten", icon: Palette },
  { id: "contract", label: "Vertrag", icon: FileText },
  { id: "referrals", label: "Empfehlungen", icon: Gift },
];

export function DashboardShell({ page, section, setSection, newLeads, canBook, packageName, onUpgrade, onRefresh, refreshing, publicUrl, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 md:flex" data-testid="dashboard-page">
      <aside className="w-full max-w-full min-w-0 md:w-64 md:flex-shrink-0 bg-white border-b md:border-b-0 md:border-r border-slate-100 md:min-h-screen md:sticky md:top-0 md:h-screen flex md:flex-col" data-testid="dashboard-sidebar">
        <div className="hidden md:block px-5 pt-6 pb-5">
          <button onClick={() => navigate("/meine-seiten")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-4" data-testid="dashboard-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Meine Seiten
          </button>
          <p className="font-bold text-slate-900 leading-tight truncate" data-testid="sidebar-company">{page?.company_name}</p>
          <p className="text-xs text-slate-500 truncate">{packageName !== "Kein Abo" ? `${packageName}-Paket` : "Kein Abo"}</p>
        </div>

        <nav className="flex min-w-0 md:flex-col gap-1 px-2 md:px-3 py-2 md:py-0 overflow-x-auto md:overflow-visible md:flex-1" data-testid="dashboard-nav">
          {NAV.map(item => {
            const active = section === item.id;
            return (
              <button key={item.id} onClick={() => setSection(item.id)} data-testid={`nav-${item.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`}>
                <item.icon className={`w-[18px] h-[18px] ${active ? "text-indigo-600" : "text-slate-400"}`} />
                <span>{item.label}</span>
                {item.id === "leads" && newLeads > 0 && <span className="ml-auto text-[11px] bg-indigo-600 text-white rounded-full px-1.5 py-0.5" data-testid="nav-leads-badge">{newLeads}</span>}
              </button>
            );
          })}
        </nav>

        <div className="hidden md:block p-4 border-t border-slate-100">
          <button
            onClick={() => canBook && setSection("support")}
            disabled={!canBook}
            title={canBook ? "Telefonisches Onboarding buchen" : "Telefonische Unterstützung ist ab dem Paket Pro verfügbar"}
            data-testid="nav-support-cta"
            className={`w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-colors ${canBook ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-200" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}>
            {canBook ? <Phone className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Unterstützung buchen
          </button>
          {!canBook && <p className="text-[11px] text-slate-500 text-center mt-2 leading-snug" data-testid="support-locked-hint">Ab Paket Pro verfügbar – im Starter-Paket nicht enthalten.</p>}
          {!canBook && onUpgrade && <button onClick={onUpgrade} className="block mx-auto mt-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline" data-testid="nav-upgrade-link">Jetzt auf Pro upgraden</button>}
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white/80 backdrop-blur border-b border-slate-100 px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => navigate("/meine-seiten")} className="md:hidden flex items-center gap-1.5 text-slate-600 text-sm"><ArrowLeft className="w-4 h-4" /></button>
          <h1 className="text-lg font-bold text-slate-900 hidden md:block">{[...NAV, { id: "support", label: "Unterstützung buchen" }].find(n => n.id === section)?.label}</h1>
          <div className="flex items-center gap-2">
            {canBook && <Button variant="outline" size="sm" className="md:hidden rounded-full h-9" onClick={() => setSection("support")} data-testid="mobile-support-btn"><Phone className="w-4 h-4" /></Button>}
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} className="rounded-full h-9 px-3" data-testid="dashboard-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /><span className="hidden sm:inline ml-2">Aktualisieren</span>
            </Button>
            <Button size="sm" onClick={() => window.open(publicUrl, "_blank")} className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-9 px-3" data-testid="dashboard-open-site-btn">
              <ExternalLink className="w-4 h-4" /><span className="hidden sm:inline ml-2">Seite öffnen</span>
            </Button>
          </div>
        </header>
        <main className="px-4 md:px-8 py-6 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
